import { HttpEventType, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DocumentData, FileSource, WebSocketChat } from '@meddev/fe-shared';
import { Store } from '@ngxs/store';
import { BookingState } from '@shared/booking-feature';
import { DocumentManagerService } from '@shared/data';
import { BehaviorSubject, EMPTY, Observable, catchError, filter, first, map, of, skip, switchMap, tap, timeout } from 'rxjs';
import { MessagingAdapterBase } from '../adapter/messaging-adapter.base';
import { ChatSource } from '../enum/chat-source.enum';
import { MESSAGING_ADAPTER } from '../token';

@Injectable({
    providedIn: 'root',
})
export class MessagingService {
    private readonly messagingAdapter: MessagingAdapterBase = inject(MESSAGING_ADAPTER);
    private readonly documentManager: DocumentManagerService = inject(DocumentManagerService);
    private readonly store = inject(Store);
    private readonly messagesSubject = new BehaviorSubject<WebSocketChat[]>([]);
    readonly messages$: Observable<WebSocketChat[]> = this.messagesSubject.asObservable();
    private readonly booking$ = this.store.select(BookingState.booking);

    /*
     * TODO
     *  - either backend should provide customer + contractor name or we should make bunch of calls just like halodoktor. getTelemedicineChat AB3 method
     *  -
     */
    constructor() {
        this.init();
    }

    sendMessage(content: string, sender = ChatSource.PATIENT, file?: File) {
        this.booking$
            .pipe(
                first(),
                map(booking => booking?.prereservationId),
                filter((guid): guid is string => !!guid),
                tap(guid => {
                    if (file) {
                        this.documentManager
                            .uploadDocument(guid, file, '')
                            .pipe(
                                filter(res => res.type === HttpEventType.Response),
                                map(res => {
                                    return { id: (res as unknown as HttpResponse<string>).body, file: file };
                                }),
                            )
                            .subscribe(res => {
                                const document: DocumentData = {
                                    documentId: res?.id ?? '',
                                    description: '',
                                    contentType: res?.file?.type,
                                    fileName: res?.file?.name,
                                    fileSource: 'DOCUMENT' as FileSource,
                                };

                                const newMessage: WebSocketChat = new WebSocketChat().deserialize({
                                    message: content,
                                    source: sender,
                                    document: document,
                                    eventTime: new Date(),
                                });

                                this.messagingAdapter.sendMessage(newMessage);
                            });
                    } else {
                        const newMessage: WebSocketChat = new WebSocketChat().deserialize({
                            message: content,
                            source: sender,
                        });

                        this.messagingAdapter.sendMessage(newMessage);
                    }
                }),
            )
            .subscribe();
    }

    init(): void {
        this.booking$
            .pipe(
                first(),
                map(booking => booking?.prereservationId),
                filter((guid): guid is string => !!guid),
                switchMap(guid =>
                    this.messagingAdapter.initWebSocket(guid).pipe(
                        switchMap(() =>
                            this.messagingAdapter.getChatData().pipe(
                                filter(data => Array.isArray(data) && data.length > 0),
                                first(),
                                timeout({
                                    first: 5000,
                                    with: () => {
                                        console.warn('[WARN] Timed out waiting for chat data, proceeding with empty data');
                                        return of([]);
                                    },
                                }),
                                switchMap(initialData => {
                                    if (initialData.length > 0) {
                                        this.messagesSubject.next(initialData);
                                    }

                                    return this.messagingAdapter.getChatData().pipe(
                                        skip(1),
                                        filter(update => Array.isArray(update) && update.length > 0),
                                        tap(update => {
                                            const currentMessages = this.messagesSubject.value;
                                            const newMessages = this.mergeMessages(currentMessages, update);
                                            this.messagesSubject.next(newMessages);
                                        }),
                                    );
                                }),
                                catchError(error => {
                                    console.error('[ERROR] Error in chat data stream', error);
                                    return EMPTY;
                                }),
                            ),
                        ),
                    ),
                ),
            )
            .subscribe();
    }

    // Helper to merge messages and avoid duplicates
    private mergeMessages(existing: WebSocketChat[], newMessages: WebSocketChat[]): WebSocketChat[] {
        const convertedMessages = newMessages.map(wsMsg => {
            return {
                eventTime: wsMsg.eventTime ?? Date.now(),
                message: wsMsg.chat ?? '',
                source: wsMsg.source === 'DOCTOR' ? ChatSource.DOCTOR : ChatSource.PATIENT,
                document: wsMsg.document ?? null,
                chat: wsMsg.chat ?? 'main',
                username: wsMsg.username ?? 'Unknown',
                userid: wsMsg.userid ?? '0',
            } as WebSocketChat;
        });

        const merged = [...existing];
        for (const msg of convertedMessages) {
            const exists = existing.some(
                existingMsg => existingMsg.eventTime === msg.eventTime && existingMsg.message === msg.message && existingMsg.userid === msg.userid,
            );

            if (!exists) {
                merged.push(msg);
            }
        }

        return merged;
    }

    closeSocket(): void {
        this.booking$
            .pipe(
                first(),
                map(booking => booking?.prereservationId),
                filter((guid): guid is string => !!guid),
            )
            .subscribe(guid => {
                this.messagesSubject.next([]);
                this.messagingAdapter.closeSocket(guid);
            });
    }
}
